<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import DatePicker from '@/components/Applications/Shared/DatePicker.vue'

const { t } = useI18n()
const { umsokn } = useUmsokn()
const props = defineProps<{ disable?: boolean }>()

// format as "MM.YYYY"
function fmtMonthYear(d: Date) {
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${mm}.${yyyy}`
}

// wrap umsokn.startLifeyrir in a computed v-model
const monthValue = computed({
  get() {
    if (!umsokn.value?.startLifeyrir) return null
    const [mm, yyyy] = umsokn.value.startLifeyrir.split('.')
    return yyyy && mm ? `01.${mm}.${yyyy}` : null
  },
  set(v: string | null) {
    if (!v || !umsokn.value) return
    // Parse DD.MM.YYYY format from DatePicker
    const parts = v.split('.')
    if (parts.length === 3) {
      const [dd, mm, yyyy] = parts
      umsokn.value.startLifeyrir = `${mm}.${yyyy}`
    } else {
      const d = new Date(v)
      umsokn.value.startLifeyrir = fmtMonthYear(d)
    }
  },
})

const rules = {
  umsokn: {
    startLifeyrir: { required },
    halfLifeyrir: { required },
  },
}

const v$ = useVuelidate(rules, { umsokn })

defineExpose({
  validate() {
    v$.value.$touch()
    return !v$.value.$invalid
  },
})
</script>

<template>
  <div v-if="umsokn" class="col-sm-12">
    <v-row>
      <v-col>
        <h2 class="forms__header">
          {{ t('lifeyri_umsokn_step3_header1') }}
        </h2>
      </v-col>
    </v-row>

    <!-- Month / Year -->
    <v-row>
      <v-col md="6">
        <DatePicker
          v-model="monthValue"
          view="month"
          hide-header
          max="2099-12-31"
          :label="t('lifeyri_umsokn_step3_date_label')"
          :placeholder="t('general_choose_date')"
          variant="outlined"
          density="comfortable"
          :readonly="props.disable"
          :error="v$.umsokn.startLifeyrir.$error"
          :error-messages="v$.umsokn.startLifeyrir.$error ? [t('lifeyri_umsokn_step3_date_label_error')] : []"
        />
      </v-col>
    </v-row>

    <!-- Full vs Half -->
    <v-row>
      <v-col>
        <div class="label-container">
          <v-radio-group
            v-model="umsokn.halfLifeyrir"
            :readonly="props.disable"
            hide-details
            :error="v$.umsokn.halfLifeyrir.$error"
            :error-messages="
              v$.umsokn.halfLifeyrir.$error
                ? [t('lifeyri_umsokn_step3_option_half_error')]
                : []
            "
          >
            <v-radio
              :label="t('lifeyri_umsokn_step3_option_full')"
              :value="t('lifeyri_umsokn_step3_option_full')"
            />
            <v-radio
              :label="t('lifeyri_umsokn_step3_option_half')"
              :value="t('lifeyri_umsokn_step3_option_half')"
            />
          </v-radio-group>
        </div>

        <div class="disclamer">
          {{ t('lifeyri_umsokn_step3_disclamer') }}
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<style scoped>
.disclamer {
  margin-top: 20px;
  color: #194458;
  line-height: 24px;
  font-weight: bold;
  font-size: 18px;
}
</style>
